import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { getMediaUrl } from '@/constants/media';
import { formatName, nameInitial } from '@/utils/formatName';
import { formatFullDate } from '@/utils/formatTime';
import { profileLink } from '@/utils/profileLink';
import './PreviouslyOwnedSection.scss';

export interface PreviouslyOwnedProfile {
  id: string;
  nftProfileId: number;
  creatorId: string;
  creatorUsername: string;
  creatorDisplayName: string;
  creatorAvatarUrl: string | null;
  currentOwnerId: string;
  currentOwnerUsername: string;
  currentOwnerDisplayName: string;
  currentOwnerAvatarUrl: string | null;
  currentPrice: number;
  lastOwnedAt: string;
  lastSalePrice: number;
  timesSold: number;
}

interface ApiResponse {
  profiles: PreviouslyOwnedProfile[];
  pagination: { limit: number; offset: number; hasMore: boolean };
}

interface UsePreviouslyOwnedResult {
  profiles: PreviouslyOwnedProfile[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const usePreviouslyOwned = (limit = 12): UsePreviouslyOwnedResult => {
  const [profiles, setProfiles] = useState<PreviouslyOwnedProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get<ApiResponse>('/marketplace/previously-owned', {
        params: { limit },
      });
      setProfiles(data.profiles);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to load previously-owned profiles');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  return { profiles, loading, error, refetch: fetchProfiles };
};

const formatPrice = (n: number): string =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface PreviouslyOwnedSectionProps {
  profiles: PreviouslyOwnedProfile[];
  loading: boolean;
  error: string | null;
  onBuyBack?: (profile: PreviouslyOwnedProfile) => void;
}

const PreviouslyOwnedSection: React.FC<PreviouslyOwnedSectionProps> = ({ profiles, loading, error, onBuyBack }) => {
  if (loading) {
    return (
      <section className="previously-owned">
        <p className="previously-owned__hint">Loading your trade history…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="previously-owned">
        <p className="previously-owned__hint previously-owned__hint--error">{error}</p>
      </section>
    );
  }

  if (profiles.length === 0) {
    return (
      <section className="previously-owned">
        <p className="previously-owned__hint">Profiles you sell will show up here.</p>
      </section>
    );
  }

  return (
    <section className="previously-owned">
      <p className="previously-owned__hint">
        Profiles you used to own. Tap the avatar to visit the creator.
      </p>

      <div className="previously-owned__grid">
        {profiles.map((p) => (
          <article key={p.id} className="previously-owned__card">
            <Link
              to={profileLink(p.creatorUsername, 'previously_owned')}
              className="previously-owned__avatar"
              aria-label={`View ${formatName({ displayName: p.creatorDisplayName, username: p.creatorUsername })}'s profile`}
            >
              {p.creatorAvatarUrl ? (
                <img
                  src={getMediaUrl(p.creatorAvatarUrl) ?? undefined}
                  alt={formatName({ displayName: p.creatorDisplayName, username: p.creatorUsername })}
                />
              ) : (
                <div className="previously-owned__avatar-placeholder">
                  {nameInitial({ displayName: p.creatorDisplayName, username: p.creatorUsername })}
                </div>
              )}
            </Link>

            <div className="previously-owned__body">
              <div className="previously-owned__name">{formatName({ displayName: p.creatorDisplayName, username: p.creatorUsername })}</div>

              <div className="previously-owned__meta">
                <span className="previously-owned__sold-on">
                  Sold {formatFullDate(p.lastOwnedAt)}
                </span>
                <span className="previously-owned__sold-price">
                  for {formatPrice(p.lastSalePrice)}
                </span>
              </div>

              <div className="previously-owned__owner">
                Now owned by{' '}
                <Link
                  to={profileLink(p.currentOwnerUsername, 'previously_owned')}
                  className="previously-owned__owner-link"
                >
                  {formatName({ displayName: p.currentOwnerDisplayName, username: p.currentOwnerUsername })}
                </Link>
              </div>

              <div className="previously-owned__price">
                Current price: <strong>{formatPrice(p.currentPrice)}</strong>
              </div>

              {onBuyBack && (
                <button
                  type="button"
                  className="previously-owned__buy-back"
                  onClick={() => onBuyBack(p)}
                >
                  Buy Back
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default PreviouslyOwnedSection;
